import { useEffect } from "react";
import { createPortal } from "react-dom";
import { T, UI, FONT } from "../constants/tokens";
import { useIsMobile } from "../utils/hooks";
import { modalScrim, modalPanel } from "./Modal";
import PriceChart from "./PriceChart";
import LTR from "./LTR";
import Icon from "./Icon";

// ─── An index, as a chart and nothing else (26/10/2026) ──────────────────────
// Opened from a cell of "שווקים". The head says what the cell said · name,
// value, the day's change, the moment and the source · and under it the same
// chart as the company page (PriceChart): ranges, line or candles, the change
// over the range, the line with a date and value under a finger, and
// TradingView's full chart. No news, no profile, no financials: an index has
// none of its own. A market that is closed shows its last session.
//
// `series`: { label, yahoo, tv, delay, points } · `cell`: what the card shows
// (MarketsCard, cellOf).
export default function IndexChartPanel({ series, cell, onClose }) {
  const isMobile = useIsMobile();
  // Escape closes this panel · unless TradingView's full chart is open over
  // it, which takes the same key for itself (PriceChart, data-full-chart).
  // The page under the panel does not scroll while it is open.
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape" && !e.defaultPrevented && !document.querySelector("[data-full-chart]")) onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const change = cell?.change;
  const flat = change == null || Math.abs(change) < 0.005;
  return createPortal(
    <div style={modalScrim(1500, { align: isMobile ? "bottom" : "center", padding: isMobile ? 0 : 16 })}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={`${series.label} · גרף`} dir="rtl"
        style={{ ...modalPanel({ variant: isMobile ? "sheet" : "dialog", width: 760 }), width: "100%", maxWidth: 760, maxHeight: isMobile ? "92dvh" : "90dvh", overflowY: "auto", padding: isMobile ? "16px 16px 20px" : "20px 22px 22px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: UI.text.primary, fontFamily: series.latin ? FONT.latin : undefined }}>
              {series.latin ? <LTR>{series.label}</LTR> : series.label}
            </div>
            {cell && (
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
                <LTR className="si-num" style={{ fontSize: 22, fontWeight: 800, color: UI.text.primary }}>{cell.display}</LTR>
                {change != null && Number.isFinite(change) && (
                  <LTR className="si-num" style={{ fontSize: 14, fontWeight: 700, color: flat ? UI.text.muted : change > 0 ? UI.gain : UI.loss }}>
                    {flat ? "" : change > 0 ? "▲ " : "▼ "}{Math.abs(change).toFixed(2)}%
                  </LTR>
                )}
                <span style={{ fontSize: 12, color: UI.text.muted }}>שינוי יומי</span>
              </div>
            )}
            {cell && (cell.when || cell.source) && (
              <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 4 }}>
                {cell.when && <LTR className="si-num">{cell.when}</LTR>}{cell.when && cell.source ? " · " : ""}{cell.source && <LTR>{cell.source}</LTR>}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="סגור"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.md, minHeight: 44, padding: "0 12px", fontSize: 13, fontWeight: 700, color: UI.text.secondary, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
            <Icon name="x" size={14} color={UI.text.secondary}/> סגור
          </button>
        </div>
        <div style={{ marginTop: 14 }}>
          <PriceChart symbol={series.yahoo} tvSymbol={series.tv} label={series.label} points={series.points !== false} delay={series.delay}/>
        </div>
        {/* What the index measures, from its provider's methodology (S&P 500,
            Nasdaq and Dow only · MarketsCard, ABOUT). */}
        {series.about && (
          <div data-index-about style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.6, marginTop: 12 }}>
            {series.about.text} · לפי מסמך המתודולוגיה של{" "}
            <a href={series.about.source.url} target="_blank" rel="noopener noreferrer" style={{ color: UI.interactive, fontWeight: 700, textDecoration: "none" }}>
              <LTR>{series.about.source.text}</LTR>
            </a>
          </div>
        )}
        <div style={{ fontSize: 11.5, color: UI.text.muted, textAlign: "center", marginTop: 12 }}>מידע בלבד · אינו ייעוץ השקעות</div>
      </div>
    </div>,
    document.body,
  );
}
